import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { t } from '../i18n'
import { Notice } from '../ui'

/**
 * Asking this instance to mail a link that sets a new password.
 *
 * **One answer, and the screen says the same sentence every time.** An address
 * nobody here has, an address belonging to somebody who has never had a
 * password, and an address that really was sent a link are told apart nowhere —
 * not by the instance, and not by this screen, which would undo the care taken
 * on the other side of the call. The sentence is written so it is true in every
 * one of those cases: if there is an account, a link is on its way.
 *
 * The form is not cleared and the button is not disabled afterwards. Somebody
 * who typed their address wrong is going to type it again, and a screen that
 * has become a paragraph makes them reload to do it.
 *
 * Outside the session gate, deliberately: the person here is by definition the
 * person who cannot sign in.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>(null)
  const [sending, setSending] = useState(false)

  async function submit(): Promise<void> {
    setSending(true)
    try {
      await api.POST('/signin/password/reset-request', { body: { email } })
      // Whatever came back. The instance answers the same way for an address it
      // has and one it does not, so a screen that read the status would be
      // inventing a difference the API refused to make.
      setState('asked')
    } catch {
      setState('unreachable')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="signin">
      <h2 className="page">{t('app.name')}</h2>
      <p className="page-sub">{t('forgot.title')}</p>

      {state === 'asked' ? <Notice tone="good">{t('forgot.sent')}</Notice> : null}
      {state === 'unreachable' ? (
        <Notice tone="bad">{t('signin.unreachable')}</Notice>
      ) : null}

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          if (sending || email.trim().length === 0) return
          setState(null)
          void submit()
        }}
      >
        <h3>{t('forgot.heading')}</h3>
        <p className="muted">{t('forgot.explain')}</p>
        <div className="field">
          <label htmlFor="email">{t('signin.password.email')}</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            spellCheck={false}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <button type="submit" className="primary" disabled={sending}>
          {t('forgot.submit')}
        </button>
        <p className="muted">{t('forgot.no_mail')}</p>
        <p className="muted">
          <Link to="/signin/reset">{t('forgot.have_a_code')}</Link>
        </p>
        <p className="muted">
          <Link to="/signin">{t('reset.back')}</Link>
        </p>
      </form>
    </div>
  )
}

type State = 'asked' | 'unreachable' | null
